if (!customElements.get('carousel-component')) {
  class Carousel extends HTMLElement {
    constructor() {
      super();
      this.carouselElement = this;
    }

    connectedCallback() {
      this.carouselElement = this;
      if (!this.carouselElement.classList.contains('splide')) return;

      if (!this.carousel) {
        requestAnimationFrame(() => {
          this.initOptions();
          this.initCarousel();
        });
      } else {
        this.carousel.refresh();
      }
    }

    initOptions() {
      this.desktopPerPage = this.carouselElement.dataset['desktopperpage'] || 4;
      this.mobilePerPage = this.carouselElement.dataset['mobileperpage'] || 1;
      this.perMove = this.carouselElement.dataset['permove'] || 1;
      this.focus = this.carouselElement.dataset['focus'] || 'center';
      this.type = this.carouselElement.dataset['type'] || 'slide';
      this.desktopType = this.carouselElement.dataset['desktoptype'] || this.type;
      this.gap = this.carouselElement.dataset['gapbetweenslides'] || 10;
      this.gapMobile = this.carouselElement.dataset['gapbetweenslidesonmobile'] || 0;
      this.autoplaySpeed = parseInt(this.dataset['autoplaySpeed']) || 3000;
      this.direction = this.carouselElement.dataset['direction'] || 'ltr';
      this.mobilePaddingLeft = this.carouselElement.dataset['mobilepaddingleft'] || '0';
      this.mobilePaddingRight = this.carouselElement.dataset['mobilepaddingright'] || '0';
      this.desktopPaddingLeft = this.carouselElement.dataset['desktoppaddingleft'] || '0';
      this.desktopPaddingRight = this.carouselElement.dataset['desktoppaddingright'] || '0';

      this.showarrows = this.carouselElement.dataset['showarrows'] === 'true' || false;
      this.autoplay = this.carouselElement.dataset['autoplay'] === 'true' || false;
      this.showarrowsonmobile = this.carouselElement.dataset['showarrowsonmobile'] === 'true' || false;
      this.showdots = this.carouselElement.dataset['showdots'] === 'true' || false;
      this.showdotsonmobile = this.carouselElement.dataset['showdotsonmobile'] === 'true' || false;
      this.isNavigation = this.carouselElement.dataset['isnavigation'] === 'true' || false;
      this.disableDrag = this.carouselElement.dataset['disabledrag'] === 'true' || false;
      this.destroyOnMobile = this.carouselElement.dataset['enableonmobile'] == 'false' || false;
      this.destroyOnDesktop = this.carouselElement.dataset['enableondesktop'] == 'false' || false;
      this.autoHeight = this.carouselElement.dataset['autoheight'] === 'true' || false;
      this.autoWidth = this.carouselElement.dataset['autowidth'] === 'true' || false;

      this.mobileBreakpoint = parseInt(this.carouselElement.dataset['mobilebreakpoint']) || 749;

      this.label = this.carouselElement.dataset['label'] || null;
      this.previousLabel = this.carouselElement.dataset['previouslabel'];
      this.nextLabel = this.carouselElement.dataset['nextlabel'];
      this.dotLabel = this.carouselElement.dataset['dotlabel'];

      this.sync = this.carouselElement.dataset['carouselsyncselector'] || false;
      this.persistPosition = this.carouselElement.dataset.persistPosition || false;
    }

    getLabelOverrides() {
      const overrides = {};

      if (this.previousLabel) overrides.prev = this.previousLabel;
      if (this.nextLabel) overrides.next = this.nextLabel;

      if (this.dotLabel) {
        overrides.slideX = this.dotLabel + ' %s';
        overrides.pageX = this.dotLabel + ' %s';
      }

      return overrides;
    }

    initCarousel() {
      if (this.destroyOnMobile && this.destroyOnDesktop) return;

      // More option available here <https://splidejs.com/documents/>
      // This slider can be customized as require check the above doc before adding any new
      // Slider library.

      this.carousel = new Splide(this.carouselElement, {
        perPage: parseInt(this.desktopPerPage),
        type: this.desktopType,
        autoplay: this.autoplay,
        interval: this.autoplaySpeed,
        gap: this.gap,
        arrows: this.showarrows,
        pagination: this.showdots,
        isNavigation: this.isNavigation,
        drag: !this.disableDrag,
        direction: this.direction,
        destroy: this.destroyOnDesktop,
        perMove: this.perMove,
        autoHeight: this.autoHeight,
        autoWidth: this.autoWidth,
        label: this.label,
        i18n: this.getLabelOverrides(),
        padding: { left: this.desktopPaddingLeft, right: this.desktopPaddingRight },
        breakpoints: {
          [this.mobileBreakpoint]: {
            padding: { left: this.mobilePaddingLeft, right: this.mobilePaddingRight },
            perPage: Number(this.mobilePerPage) || 1,
            type: this.type,
            arrows: this.showarrowsonmobile,
            pagination: this.showdotsonmobile,
            gap: this.gapMobile,
            destroy: this.destroyOnMobile,
            autoHeight: this.autoHeight,
          }
        },
      });

      this.carousel.on('mounted moved updated resized', () => this.announcePosition());

      if (this.persistPosition) {
        this.initPersistedPosition();
      }

      if (this.sync) {
        this.initCarouselSync()
      } else {
        this.carousel.mount();
      }
    }

    initPersistedPosition() {
      const storageKey = `kl:carousel-position:${this.persistPosition}`;

      this.carousel.on('mounted', () => {
        try {
          const savedIndex = Number(sessionStorage.getItem(storageKey));

          if (Number.isInteger(savedIndex) && savedIndex >= 0) {
            this.carousel.go(savedIndex);
          }
        } catch (error) {
          console.debug('carousel: could not restore the position', { error });
        }
      });

      this.carousel.on('moved', (newIndex) => {
        try {
          sessionStorage.setItem(storageKey, String(newIndex));
        } catch (error) {
          console.debug('carousel: could not save the position', { error });
        }
      });
    }

    announcePosition() {
      try {
        this.dispatchEvent(
          new CustomEvent('kl:carousel:moved', {
            bubbles: true,
            detail: {
              index: this.carousel.index,
              endIndex: this.carousel.Components.Controller.getEnd(),
              isLooped: this.carousel.is('loop'),
            },
          })
        );
      } catch (error) {
        console.debug('carousel: could not announce the position', { id: this.id, error });
      }
    }

    initCarouselSync() {
      this.syncElement = document.querySelector(this.sync);
      this.direction = this.syncElement.dataset['direction'] || 'ltr';

      this.carouselSync = new Splide(this.sync, {
        updateOnMove: true,
        perPage: parseInt(this.syncElement.dataset['desktopperpage'] || 5),
        type: this.syncElement.dataset['type'] || 'loop',
        focus: this.syncElement.dataset['focus'] || 'center',
        isNavigation: this.syncElement.dataset['isnavigation'] === 'true' || false,
        pagination: false,
        arrows: this.syncElement.dataset['showarrows'] === 'true' || false,
        ...(this.direction === 'ttb' && {
          fixedWidth: 89,
          fixedHeight: 114,
          height: this.getPrimarySlideFirstImageHeight(),
          direction: 'ttb',
          autoHeight: true,
        }),
        breakpoints: {
          749: {
            height: 'auto',
            direction: 'ltr',
            focus: 'center',
            start: 2,
            gap: parseInt(this.syncElement.dataset['gapmobile'] || 5),
            perPage: parseInt(this.syncElement.dataset['mobileperpage'] || 5),
          }
        },
      });


      this.carousel.sync(this.carouselSync);
      this.carousel.mount();
      this.carouselSync.mount();
    };

    refreshSlider() {
      if (!this.carousel) return;

      this.carousel.refresh();
      if (this.carouselSync) {
        this.carouselSync.refresh();
      }
    }

    getPrimarySlideFirstImageHeight() {
      const image = this.querySelector('.splide__list img');
      const containerWidth = this.querySelector('.splide__list').clientWidth;

      const originalWidth = image.getAttribute('width');
      const originalHeight = image.getAttribute('height');

      return (containerWidth / originalWidth) * originalHeight;
    }
  }

  window.Carousel = Carousel;
  customElements.define('carousel-component', Carousel);
}
